import React, { FunctionComponent, useEffect, useState } from 'react'
import styled from 'styled-components'
import Column from '../../../../../components/Column';
import Row from '../../../../../components/Row';
import Text from '../../../../../components/Text';
import Button from '../../../../../components/Button';
import { useHubspotHandlers } from '../../../../../services/actions/hubspot/useHubspotHandlers';
import { useHubspotStore } from '../../../../../stores/hubspot/useHubspotStore';
import { styles } from '../../../../../styles/themes/style';
import TriggersHubspot from './TriggersHubspot';
import SvgFragment from '../../../../../fragments/SvgFragment';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppHandlers } from '../../../../../services/actions/app/useAppHandlers';
import ActionText from '../../../../../components/ActionText';
import { AppRoutes } from '../../../../../interfaces/store/appStore';

interface IHubspotProps { }

const Hubspot: FunctionComponent<IHubspotProps> = () => {
    const navigate = useNavigate()
    const { 
        initiateOAuthHandler, 
        getOAuthStatusHandler,
        reauthorizeWithOptionalScopesHandler,
        disconnectOAuthHandler,
        getHubspotTriggersHandler
    } = useHubspotHandlers()
    
    const { setDestructiveModal } = useAppHandlers()
    
    const [isConnected, setIsConnected] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [hubDomain, setHubDomain] = useState('')
    const [hubId, setHubId] = useState('')
    const [isOAuth, setIsOAuth] = useState(false)
    const [hasOptionalScopes, setHasOptionalScopes] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [searchParams] = useSearchParams()

    useEffect(() => {
        handleOAuthCallback()
        checkConnectionStatus()
    }, [])

    useEffect(() => {
        if (isConnected) {
            getHubspotTriggersHandler()
        }
    }, [isConnected])

    const handleOAuthCallback = () => {
        // Check if we're returning from OAuth
        const success = searchParams.get('success')
        const error = searchParams.get('error')
        const hub_domain = searchParams.get('hub_domain')
        const missing = searchParams.get('missing')

        if (success === 'true') {
            if (hub_domain) {
                setHubDomain(decodeURIComponent(hub_domain))
            }
            // Check if optional scopes were granted
            const hasOptionalScopesParam = searchParams.get('hasOptionalScopes')
            if (hasOptionalScopesParam) {
                setHasOptionalScopes(hasOptionalScopesParam === 'true')
            }
            // Will be set by checkConnectionStatus
            // Remove query params from URL
            window.history.replaceState({}, '', window.location.pathname)
            
            // Recheck status after OAuth success
            setTimeout(() => checkConnectionStatus(), 500)
        } else if (error) {
            let errorMessage = ''
            
            if (error === 'missing_required_scopes') {
                const missingScopes = missing ? decodeURIComponent(missing).split(',') : []
                errorMessage = 'Authorization failed: Your HubSpot account lacks access to required permissions. '
                if (missingScopes.length > 0) {
                    errorMessage += `Missing scopes: ${missingScopes.join(', ')}. `
                }
                errorMessage += 'Please ensure your HubSpot account has the necessary subscription tier and permissions, or contact your HubSpot administrator.'
            } else {
                const errorMessages: {[key: string]: string} = {
                    'missing_parameters': 'Authorization failed: Missing parameters',
                    'invalid_state': 'Authorization failed: Invalid state parameter',
                    'token_exchange_failed': 'Failed to obtain access token from HubSpot'
                }
                errorMessage = errorMessages[error] || `Authorization error: ${error}`
            }
            
            setErrorMessage(errorMessage)
            // Remove query params from URL
            //window.history.replaceState({}, '', window.location.pathname)
        }
    }

    const checkConnectionStatus = async () => {
        setIsLoading(true)
        setErrorMessage('')
        try {
            const result = await getOAuthStatusHandler()
            
            if (result?.connected) {
                setIsConnected(true)
                setHubDomain(result.hubDomain || '')
                setHubId(result.hubId || '')
                setIsOAuth(result.isOAuth || false)
                setHasOptionalScopes(result.hasOptionalScopes || false)
            } else {
                setIsConnected(false)
                setHubDomain('')
                setHubId('')
                setIsOAuth(false)
                setHasOptionalScopes(false)
            }
        } catch (error) {
            console.error('Failed to check connection status:', error)
            setErrorMessage('Failed to check connection status')
        } finally {
            setIsLoading(false)
        }
    }

    const handleConnect = async () => {
        setIsLoading(true)
        setErrorMessage('')
        try {
            await initiateOAuthHandler()
            // The handler will redirect to HubSpot
        } catch (error) {
            console.error('Failed to initiate OAuth:', error)
            setErrorMessage('Failed to initiate OAuth flow. Please try again.')
            setIsLoading(false)
        }
    }

    const handleDisconnect = () => {
        setDestructiveModal({
                headingText: 'Disconnect HubSpot?',
                descriptionText: 'Are you sure you want to disconnect HubSpot?',
                confirmBtnTitle: 'Disconnect',
                cancelBtnTitle: 'Cancel',
                onConfirmHandler: async () => {
                    setIsLoading(true)
                    setErrorMessage('')
                    try {
                        await disconnectOAuthHandler()
                        setIsConnected(false)
                        setHubDomain('')
                        setHubId('')
                        setIsOAuth(false)
                    } catch (error) {
                        console.error('Failed to disconnect:', error)
                        setErrorMessage('Failed to disconnect. Please try again.')
                    } finally {
                        setIsLoading(false)
                    }
                },
                onCancelHandler: () => {}
            })
    }

    const renderConnectionStatus = () => {
        if (isLoading && !errorMessage) {
            return (
                <StatusCard>
                    <Column gap='12px' alignItems>
                        <SvgFragment type='loading' />
                        <Text>Checking connection status...</Text>
                    </Column>
                </StatusCard>
            )
        }

        if (isConnected) {
            return (
                <StatusCard>
                    <Column gap='20px'>
                        <Row gap='12px' alignItems>
                            <StatusIcon $connected>
                                <SvgFragment type='exportHubspot' />
                            </StatusIcon>
                            <Column gap='4px'>
                                <Text $heading5>Connected to HubSpot</Text>
                                {hubDomain && <Text $lighter>{hubDomain}</Text>}
                                {hubId && <Text $lighter>Portal ID: {hubId}</Text>}
                            </Column>
                        </Row>
                        
                        <InfoBox>
                            <Column gap='10px'>
                                <Text $bold>Active Features:</Text>
                                <FeatureList>
                                    <FeatureItem>
                                        <SvgFragment type='check-small' />
                                        <Text>Sync contacts into hubspot</Text>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <SvgFragment type='check-small' />
                                        <Text>Add to segments (lists), deals and workflows</Text>
                                    </FeatureItem>
                                    {hasOptionalScopes && (
                                        <FeatureItem>
                                            <SvgFragment type='check-small' />
                                            <Text>Inbox export (custom channels)</Text>
                                        </FeatureItem>
                                    )}
                                </FeatureList>
                                {isOAuth && (
                                    <Text $lighter>
                                        Using OAuth 2.0 • Tokens refresh automatically
                                    </Text>
                                )}
                            </Column>
                        </InfoBox>

                        {!hasOptionalScopes && isOAuth && (
                            <InfoBox style={{ background: '#FFF4E6', borderColor: '#FFA500' }}>
                                <Column gap='12px'>
                                    <Row gap='8px' alignItems>
                                        <SvgFragment type='alert-triangle' />
                                        <Text $bold>Limited Permissions</Text>
                                    </Row>
                                    <Text>
                                        Your connection doesn't have access to inbox export features. 
                                        If your HubSpot account supports it, you can request additional permissions.
                                    </Text>
                                    <ActionText 
                                        onClickHandler={async () => {
                                            setIsLoading(true)
                                            setErrorMessage('')
                                            try {
                                                await reauthorizeWithOptionalScopesHandler()
                                            } catch (error) {
                                                console.error('Failed to re-authorize:', error)
                                                setErrorMessage('Failed to start re-authorization. Please try again.')
                                                setIsLoading(false)
                                            }
                                        }}
                                        $disabled={isLoading}
                                    >
                                        Request Additional Permissions
                                    </ActionText>
                                </Column>
                            </InfoBox>
                        )}

                        <Row gap='12px'>
                            <Button 
                                $type='red' 
                                onClickHandler={handleDisconnect}
                                disabled={isLoading}
                            >
                                Disconnect HubSpot
                            </Button>
                        </Row>
                    </Column>
                </StatusCard>
            )
        }

        return (
            <StatusCard>
                <Column gap='20px'>
                    {errorMessage && (
                        <ErrorBox>
                            <Row gap='8px' alignItems>
                                <SvgFragment type='alert-triangle' />
                                <Text $red>{errorMessage}</Text>
                            </Row>
                        </ErrorBox>
                    )}

                    <Row gap='12px' alignItems>
                        <StatusIcon>
                            <SvgFragment type='exportHubspot' />
                        </StatusIcon>
                        <Column gap='4px'>
                            <Text $heading5>Connect to HubSpot</Text>
                            <Text $lighter>Sync your contacts and automate your CRM workflow</Text>
                        </Column>
                    </Row>

                    <InfoBox>
                        <Column gap='12px'>
                            <Text $bold>How it works:</Text>
                            <OrderedList>
                                <li>
                                    <Text>Click "Connect with HubSpot" below</Text>
                                </li>
                                <li>
                                    <Text>Authorize LeadDelta in your HubSpot account</Text>
                                </li>
                                <li>
                                    <Text>Import contacts to Hubspot</Text>
                                </li>
                            </OrderedList>
                            
                            <Text $lighter>
                                Note: You'll need admin access to your HubSpot account
                            </Text>
                        </Column>
                    </InfoBox>

                    <Column gap='12px'>
                        <Button 
                            $type='blue' 
                            onClickHandler={handleConnect}
                            disabled={isLoading}
                            isLoading={isLoading}
                        >
                            Connect with HubSpot
                        </Button>

                        <Text $lighter>
                            🔒 Secure OAuth 2.0 authentication • Your credentials are never stored
                        </Text>
                    </Column>
                </Column>
            </StatusCard>
        )
    }

    return (
        <Container>
            <Column gap='20px'>
                <Header>
                    <Column gap='8px'>
                        <Text $heading4>HubSpot Integration</Text>
                        <Text $paragraph1 $lighter>
                            Connect your HubSpot account to automatically sync contacts, create deals, and manage your CRM data.
                        </Text>
                        {isConnected && (
                            <ActionText onClickHandler={() => navigate(AppRoutes.SETTINGS_HUBSPOT)}>
                                Configure custom field mapping
                            </ActionText>
                        )}
                    </Column>
                </Header>

                {renderConnectionStatus()}
            </Column>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    padding: 16px;
`

const Header = styled.div`
    padding-bottom: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.quinaryColor};
`

const StatusCard = styled.div`
    background: ${({ theme }) => theme.secondaryColor};
    border: 1px solid ${({ theme }) => theme.quinaryColor};
    border-radius: 8px;
    padding: 24px;
    max-width: 600px;
`

const StatusIcon = styled.div<{ $connected?: boolean }>`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $connected }) => $connected ? styles.colors.green200 : styles.colors.green200};
    color: ${({ $connected }) => $connected ? styles.colors.green600 : styles.colors.orange600};
`

const InfoBox = styled.div`
    background: ${({ theme }) => theme.tertiaryColor};
    border: 1px solid ${({ theme }) => theme.quinaryColor};
    border-radius: 6px;
    padding: 16px;

    ul {
        margin: 0;
        padding-left: 20px;
        
        li {
            margin: 4px 0;
        }
    }
`

const ErrorBox = styled.div`
    background: #FEE;
    border: 1px solid ${styles.colors.red600};
    border-radius: 6px;
    padding: 12px 16px;
`

const FeatureList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const FeatureItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
        flex-shrink: 0;
        color: ${styles.colors.green600};
    }
`

const OrderedList = styled.ol`
    margin: 0;
    padding-left: 20px;
    
    li {
        margin: 6px 0;
    }
`

export default Hubspot;

